import { NoResultComponent } from '@/app/components/shared/no-result/no-result.component';
import { TranslationService } from '@/app/services/translation.service';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { MatchStandings, TeamStanding } from '../../models';

@Component({
  selector: 'app-standings-table',
  standalone: true,
  imports: [
    CommonModule,
    NzGridModule,
    NzTableModule,
    NzToolTipModule,
    TranslateModule,
    NoResultComponent,
  ],
  templateUrl: './standings-table.component.html',
  styleUrl: './standings-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandingsTableComponent {
  standings = input.required<MatchStandings>();
  homeId = input.required<string>();
  awayId = input.required<string>();

  translationService = inject(TranslationService);

  tableHeaders = [
    {
      title: '#',
      compare: (a: TeamStanding, b: TeamStanding) => a.Position - b.Position,
      priority: 1,
      show: true,
      tooltip: 'Position',
      fixed: true,
      width: '15px',
      align: 0,
    },
    {
      title: 'Team',
      compare: (a: TeamStanding, b: TeamStanding) =>
        this.translationService
          .getTraduccionName(a.NameTranslation, a.Name)
          .localeCompare(
            this.translationService.getTraduccionName(b.NameTranslation, b.Name)
          ),
      priority: 2,
      show: true,
      tooltip: 'Team',
      fixed: true,
      width: '80px',
      align: 0,
    },
    {
      title: 'GP',
      compare: (a: TeamStanding, b: TeamStanding) => {
        if (a.Played === b.Played) {
          return a.Position - b.Position;
        }

        return b.Played - a.Played;
      },
      priority: 2,
      show: true,
      tooltip: 'Matches',
      fixed: false,
      width: '20px',
      align: 2,
    },
    {
      title: 'W',
      compare: (a: TeamStanding, b: TeamStanding) => {
        if (a.Won === b.Won) {
          return a.Position - b.Position;
        }

        return b.Won - a.Won;
      },
      priority: 2,
      show: true,
      tooltip: 'Win',
      fixed: false,
      width: '20px',
      align: 2,
    },
    {
      title: 'T',
      compare: (a: TeamStanding, b: TeamStanding) => {
        if (a.Draw === b.Draw) {
          return a.Position - b.Position;
        }
        return b.Draw - a.Draw;
      },
      priority: 2,
      show: true,
      tooltip: 'Tied',
      fixed: false,
      width: '20px',
      align: 2,
    },
    {
      title: 'L',
      compare: (a: TeamStanding, b: TeamStanding) => {
        if (a.Loss === b.Loss) {
          return a.Position - b.Position;
        }
        return b.Loss - a.Loss;
      },
      priority: 2,
      show: true,
      tooltip: 'Loss',
      fixed: false,
      width: '20px',
      align: 2,
    },
    {
      title: 'GD',
      compare: (a: TeamStanding, b: TeamStanding) => {
        if (a.GoalsDifference === b.GoalsDifference) {
          return a.Position - b.Position;
        }
        return b.GoalsDifference - a.GoalsDifference;
      },
      priority: 2,
      show: true,
      tooltip: 'Diff',
      fixed: false,
      width: '24px',
      align: 2,
    },
    {
      title: 'PTS',
      compare: (a: TeamStanding, b: TeamStanding) => {
        if (a.Points === b.Points) {
          return a.Position - b.Position;
        }
        return b.Points - a.Points;
      },
      priority: 2,
      show: true,
      tooltip: 'Points',
      fixed: false,
      width: '30px',
      align: 2,
    },
  ];

  getTeamStandings() {
    return this.standings()?.Teams ?? [];
  }

  getPromotions() {
    return this.standings()?.Promotions ?? [];
  }

  getNameTranslation(
    translations: Record<string, string> | null,
    defaultName: string
  ) {
    return this.translationService.getTraduccionName(translations, defaultName);
  }

  higthLigth(id: string) {
    return id === this.homeId() || id === this.awayId();
  }
}
